import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Loader2, Newspaper, ExternalLink, Calendar } from "lucide-react";

interface AccountNewsTabProps {
  accountId: string;
  companyName: string;
}

export function AccountNewsTab({ accountId, companyName }: AccountNewsTabProps) {
  const { data: news, isLoading } = useQuery({
    queryKey: ["account-news", accountId, companyName],
    queryFn: async () => {
      // Primary: use company_id FK (indexed, reliable)
      // Fallback: text match on prospect_company_name or companies_mentioned
      const { data, error } = await supabase
        .from("news_intelligence")
        .select("id, title, url, published_date, source, short_summary, signal_type, priority_level, is_prospect_opportunity, is_competitor_alert")
        .or(`company_id.eq.${accountId},prospect_company_name.ilike.%${companyName}%,companies_mentioned.cs.{${companyName}}`)
        .order("published_date", { ascending: false })
        .limit(25);
      if (error) throw error;
      return data || [];
    },
    enabled: !!companyName,
  });

  if (isLoading) return <div className="flex items-center justify-center h-[300px]"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!news?.length) return <div className="p-8 text-center"><Newspaper className="h-12 w-12 mx-auto text-muted-foreground mb-4" /><h3 className="text-lg font-medium mb-2">No News Found</h3><p className="text-muted-foreground text-sm">No news articles matched for {companyName}</p></div>;

  return (
    <div className="divide-y">
      {news.map((article) => (
        <div key={article.id} className="p-4 hover:bg-muted/50 transition-colors">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {article.signal_type && <Badge variant="outline" className="text-xs">{article.signal_type}</Badge>}
                {article.is_prospect_opportunity && <Badge className="text-xs bg-green-100 text-green-800 border-green-200">Opportunity</Badge>}
                {article.is_competitor_alert && <Badge className="text-xs bg-red-100 text-red-800 border-red-200">Competitor</Badge>}
                {article.priority_level && <Badge variant="secondary" className="text-xs">{article.priority_level}</Badge>}
              </div>
              <a href={article.url || "#"} target="_blank" rel="noopener noreferrer" className="text-sm font-medium hover:text-primary hover:underline flex items-center gap-1">
                {article.title}<ExternalLink className="h-3 w-3 shrink-0" />
              </a>
              {article.short_summary && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{article.short_summary}</p>}
              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                {article.source && <span>{article.source}</span>}
                {article.published_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(article.published_date).toLocaleDateString()}</span>}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
